import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Link, useParams } from 'react-router-dom'
import classnames from 'classnames'
import { getTenant, mergeAccountInvoices } from '../Tenants.slice'
import { pushToast } from '../../../../components/Toaster/Toaster.slice'
import {
  getStatementApi,
  updateInvoiceApi,
  checkDOBAvailabilityBulkApi,
  downloadPGEDOBWithFilenameApi,
  getInvoiceListApi,
} from '../../../../apis/apis'
import Modal from '../../../../components/Modal'
import InlineEditInput from '../../../../components/Form/InlineEditInput'
import InlineEditDatePicker from '../../../../components/Form/InlineEditDatePicker'
import Table, { type TableColumnConfig } from '../../../../components/Table/Table'
import Badge from '../../../../components/Badge'
import { formatUTCDate, formatCurrency, dateFromUTCDateString, formatBillingPeriod } from '../../../../util/string'
import { CollectionsInvoice, Paginated, StatementType } from '../../../../apis/types'
import { useAppDispatch, useAppSelector } from '../../../../shared/redux/hooks'
import {
  getStatementTypeVisibility,
  hasDistinctLateByDate,
  InvoiceStatementIcons,
  statementColumnWidthFor,
} from '../../../collections/invoices/invoiceStatementIcons'

function formatInvoiceCurrency(value: number | null | undefined): React.ReactNode {
  if (value == null || isNaN(Number(value))) {
    return <span className="text-slate-400 dark:text-slate-500">—</span>
  }
  return (
    <span className={classnames('tabular-nums', value < 0 && 'text-red-600 dark:text-red-400')}>{formatCurrency(value)}</span>
  )
}

function TruncatedCellWithTooltip({
  text,
  maxWidthClass,
}: {
  text: string
  maxWidthClass: string
}): React.ReactElement | null {
  const anchorRef = useRef<HTMLSpanElement>(null)
  const [visible, setVisible] = useState(false)
  const [coords, setCoords] = useState({ top: 0, left: 0 })

  if (!text) {
    return null
  }

  const showTooltip = (): void => {
    const el = anchorRef.current
    if (el == null || el.scrollWidth <= el.clientWidth) {
      return
    }
    const rect = el.getBoundingClientRect()
    setCoords({ top: rect.bottom + 4, left: rect.left })
    setVisible(true)
  }

  return (
    <>
      <span
        ref={anchorRef}
        className={classnames(maxWidthClass, 'truncate block')}
        onMouseEnter={showTooltip}
        onMouseLeave={() => setVisible(false)}
      >
        {text}
      </span>
      {visible &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-[9999] max-w-sm rounded-md bg-slate-600 px-2 py-1 text-xs font-normal normal-case text-white shadow-lg dark:bg-slate-950"
            style={{ top: coords.top, left: coords.left }}
          >
            {text}
          </div>,
          document.body
        )}
    </>
  )
}

function formatPaymentMethod(invoice: CollectionsInvoice): string | null {
  if (invoice.paymentMethod === null) {
    return null
  }
  const { type, providerName, last4 } = invoice.paymentMethod
  return type === 'CHECK' ? 'Check' : type === 'DIRECT_DEPOSIT' ? 'Direct deposit' : `${providerName} (••••${last4})`
}

const MUTED_INVOICE_STATUSES = ['PAID', 'VOID', 'PAID_EXTERNALLY', 'SKIPPED', 'BALANCE_CARRY_FORWARD']

// Helper to get disabled reason for date editing
const getDateEditDisabledReason = (invoice: CollectionsInvoice): string | undefined => {
  if (!invoice.canEditDates) {
    if (!invoice.isLatestInvoice) {
      return undefined
    }
    if (MUTED_INVOICE_STATUSES.includes(invoice.status)) {
      return `Cannot edit dates for ${invoice.status} invoice`
    }
    if (invoice.remainingBalance <= 0) {
      return 'Cannot edit dates for invoice with zero balance'
    }
    return 'Cannot edit dates due to pending payment'
  }
  return undefined
}

const createDateWarningCheck = (invoice: CollectionsInvoice, field: 'dueDate' | 'lateByDate') => {
  return (value: string | null): string | null => {
    if (!value) return null
    const newDate = new Date(value)
    const billingEnd = new Date(invoice.billingEnd)
    const diffDays = Math.round((newDate.getTime() - billingEnd.getTime()) / (1000 * 60 * 60 * 24))

    if (field === 'dueDate' && diffDays > 90) {
      return `Warning: Due date is ${diffDays} days from billing period end (>90 days). Continue?`
    }
    if (field === 'lateByDate' && diffDays > 120) {
      return `Warning: Late by date is ${diffDays} days from billing period end (>120 days). Continue?`
    }
    return null
  }
}

export interface TabInvoicesProps {
  title: string
  badge?: number | string
  data: Paginated<CollectionsInvoice>
  tenantAccountId: string
  utilityName?: string
  /** When true, invoice list was loaded via getInvoiceListApi for this account. */
  invoicesEnriched?: boolean
}

export const TabInvoices: React.FC<TabInvoicesProps> = (props) => {
  const [confirmModal, setConfirmModal] = useState<React.ReactElement | boolean>(false)
  const [dobAvailability, setDobAvailability] = useState<Record<string, boolean>>({})
  const [enrichError, setEnrichError] = useState(false)
  const [enrichAttempt, setEnrichAttempt] = useState(0)
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null)
  const [dropdownCoords, setDropdownCoords] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const dropdownTriggerRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null)

  const dispatch = useAppDispatch()
  const { permissions } = useAppSelector((state) => state.app)

  const { tenantId } = useParams()
  const [currentTenantId, setCurrentTenantId] = useState(tenantId)

  const hasEditInvoicePermission = (): boolean => (permissions != null ? permissions.includes('billing:edit:invoice') : false)
  const isPgeAccount = props.utilityName === 'PG&E'

  useEffect(() => {
    setCurrentTenantId(tenantId)
  }, [tenantId])

  useEffect(() => {
    setEnrichError(false)
  }, [props.tenantAccountId, props.invoicesEnriched, enrichAttempt])

  useEffect(() => {
    if (props.invoicesEnriched) {
      return
    }
    let cancelled = false
    const enrich = async () => {
      try {
        const enriched = await getInvoiceListApi({
          tenantAccountId: props.tenantAccountId,
          resultsPerPage: props.data.resultsPerPage ?? 5,
          page: 0,
        })
        if (!cancelled) {
          dispatch(
            mergeAccountInvoices({
              accountId: props.tenantAccountId,
              invoices: enriched,
            })
          )
        }
      } catch (error) {
        console.error('Failed to enrich invoices for account', props.tenantAccountId, error)
        if (!cancelled) {
          setEnrichError(true)
        }
      }
    }
    void enrich()
    return () => {
      cancelled = true
    }
  }, [props.tenantAccountId, props.invoicesEnriched, props.data.resultsPerPage, dispatch, enrichAttempt])

  useEffect(() => {
    if (currentTenantId && isPgeAccount && props.data.results.length > 0) {
      const checkAllInvoicesForDOB = async () => {
        try {
          const dateRanges = props.data.results.map((invoice) => ({
            billingStart: invoice.billingStart,
            billingEnd: invoice.billingEnd,
          }))

          const availabilityByDateRange = await checkDOBAvailabilityBulkApi(props.tenantAccountId, dateRanges)

          const availability: Record<string, boolean> = {}
          props.data.results.forEach((invoice) => {
            const dateRangeKey = `${invoice.billingStart}-${invoice.billingEnd}`
            availability[invoice.id] = availabilityByDateRange[dateRangeKey] || false
          })

          setDobAvailability(availability)
        } catch (error) {
          console.error('Failed to check DOB availability:', error)
          const availability: Record<string, boolean> = {}
          props.data.results.forEach((invoice) => {
            availability[invoice.id] = false
          })
          setDobAvailability(availability)
        }
      }

      checkAllInvoicesForDOB()
    }
  }, [currentTenantId, isPgeAccount, props.data.results, props.tenantAccountId])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!activeDropdownId) {
        return
      }

      const target = event.target as Node
      const triggerElement = dropdownTriggerRefs.current[activeDropdownId]
      const menuElement = dropdownMenuRef.current

      if (triggerElement?.contains(target) || menuElement?.contains(target)) {
        return
      }

      setActiveDropdownId(null)
    }

    const handleScroll = () => {
      if (activeDropdownId) {
        setActiveDropdownId(null)
      }
    }

    if (activeDropdownId) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('scroll', handleScroll, true)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('scroll', handleScroll, true)
      }
    }
  }, [activeDropdownId])

  const toggleDropdown = useCallback((invoiceId: string, buttonElement: HTMLButtonElement) => {
    if (activeDropdownId === invoiceId) {
      setActiveDropdownId(null)
      return
    }

    const rect = buttonElement.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth
    const dropdownHeight = 280
    const dropdownWidth = 240

    const spaceBelow = viewportHeight - rect.bottom
    const spaceAbove = rect.top
    const spaceRight = viewportWidth - rect.left
    const spaceLeft = rect.left

    let finalY: number
    if (spaceBelow >= dropdownHeight + 20) {
      finalY = rect.bottom + 4
    } else if (spaceAbove >= dropdownHeight + 20) {
      finalY = rect.top - dropdownHeight - 4
    } else if (spaceBelow >= spaceAbove) {
      finalY = Math.max(viewportHeight - dropdownHeight - 20, rect.bottom + 4)
    } else {
      finalY = Math.max(20, rect.top - dropdownHeight - 4)
    }

    let finalX: number
    if (spaceRight >= dropdownWidth + 20) {
      finalX = rect.left
    } else if (spaceLeft >= dropdownWidth + 20) {
      finalX = rect.right - dropdownWidth
    } else {
      finalX = Math.max(20, Math.min(viewportWidth - dropdownWidth - 20, rect.left))
    }

    setDropdownCoords({ x: finalX, y: finalY })
    setActiveDropdownId(invoiceId)
  }, [activeDropdownId])

  const handleNextStatusAction = async (record: CollectionsInvoice, status: string): Promise<void> => {
    setActiveDropdownId(null)
    try {
      await new Promise((resolve, reject) => {
        setConfirmModal(<Modal resolve={resolve} reject={reject} />)
      })
      setConfirmModal(false)
      try {
        await updateInvoiceApi(record.id, { status })
        dispatch(
          pushToast({
            type: 'success',
            message: 'Invoice status updated',
          })
        )
        dispatch(getTenant(currentTenantId ?? ''))
      } catch (e: any) {
        dispatch(
          pushToast({
            type: 'error',
            message: 'Error updating invoice status',
            description: `${e.response?.data?.message !== undefined ? `${e.response?.data?.message as string}:` : 'Error:'} ${e.message as string}`,
          })
        )
      }
    } catch (e) {
      setConfirmModal(false)
    }
  }

  const openStatement = async (statementId: string, type: StatementType) => {
    try {
      const statementFile = await getStatementApi(statementId, type)
      const statementFileBlob: Blob = new Blob([statementFile], {
        type: 'application/pdf',
      })
      const statementFileUrl: string = window.URL.createObjectURL(statementFileBlob)
      window.open(statementFileUrl, '_blank')
    } catch (e) {
      await dispatch(
        pushToast({
          type: 'error',
          message: 'Error loading statement',
          description: `${
            (e as XMLHttpRequest).response?.data?.message !== undefined ? `${(e as XMLHttpRequest).response?.data?.message as string}:` : 'Error:'
          } ${(e as { message: string }).message}`,
        })
      )
    }
  }

  const handleDOBDownload = async (invoice: CollectionsInvoice) => {
    try {
      const { data, filename } = await downloadPGEDOBWithFilenameApi(props.tenantAccountId, invoice.billingStart, invoice.billingEnd)

      const isPDF = filename.endsWith('.pdf')

      if (isPDF) {
        const blob = new Blob([data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => {
          window.URL.revokeObjectURL(url)
        }, 1000)
      } else {
        const blob = new Blob([data], { type: 'application/zip' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error: any) {
      console.error('Failed to download DOB:', error)
      dispatch(
        pushToast({
          type: 'error',
          message: 'Failed to download DOB',
          description: error.response?.data?.message ?? error.response?.message ?? error.message,
        })
      )
    }
  }

  const showLateByColumn = useMemo(() => hasDistinctLateByDate(props.data.results), [props.data.results])
  const showActionsColumn = props.data.results.some((invoice) => invoice.nextStatus?.length > 0)
  const statementTypeVisibility = useMemo(
    () =>
      getStatementTypeVisibility(
        props.data.results,
        dobAvailability,
        () => isPgeAccount,
        Boolean(currentTenantId && isPgeAccount)
      ),
    [props.data.results, dobAvailability, isPgeAccount, currentTenantId]
  )
  const statementColumnWidth = statementColumnWidthFor(statementTypeVisibility)

  const colConfig = useMemo((): Array<TableColumnConfig<CollectionsInvoice>> => {
    const columns: Array<TableColumnConfig<CollectionsInvoice>> = []

    if (showActionsColumn) {
      columns.push({
        label: '',
        isFrozen: true,
        frozenMinWidth: 36,
        contentClassName: 'w-7 flex justify-center',
        render: (rec) =>
          rec.nextStatus?.length > 0 ? (
            <div className="relative w-7 flex justify-center">
              <div
                className="relative"
                ref={(el) => {
                  dropdownTriggerRefs.current[rec.id] = el
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleDropdown(rec.id, e.currentTarget)
                  }}
                  className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Change status"
                >
                  <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                </button>
              </div>
            </div>
          ) : null,
      })
    }

    columns.push(
      {
        label: 'Stmt #',
        headerTitle: 'Statement Number',
        isFrozen: true,
        frozenMinWidth: 80,
        cellClassName: 'whitespace-nowrap',
        render: (rec) => rec.number,
      },
      {
        label: 'Stmt Date',
        headerTitle: 'Statement Date',
        isFrozen: true,
        frozenMinWidth: 88,
        align: 'right',
        cellClassName: 'tabular-nums whitespace-nowrap',
        render: (rec) => formatUTCDate(new Date(rec.statementDate)),
      },
      {
        label: 'Pmt Status',
        headerTitle: 'Payment Status',
        isFrozen: true,
        frozenMinWidth: 128,
        cellClassName: 'whitespace-nowrap',
        render: (rec) => <Badge status={rec.paymentStatus} />,
      },
      {
        label: 'Period',
        headerTitle: 'Billing Period',
        columnWidth: 128,
        cellClassName: 'tabular-nums whitespace-nowrap',
        render: (rec) => formatBillingPeriod(rec.billingStart, rec.billingEnd),
      },
      {
        label: 'Due',
        headerTitle: 'Due Date',
        columnWidth: 88,
        cellClassName: 'whitespace-nowrap',
        render: (rec) => (
          <InlineEditDatePicker
            name="dueDate"
            label="Due Date"
            hideLabel
            displayFormat="monthDay"
            className="min-w-[72px]"
            initialValue={rec.due}
            minDate={new Date()}
            disabled={!rec.canEditDates || !hasEditInvoicePermission()}
            disabledReason={getDateEditDisabledReason(rec)}
            warningCheck={createDateWarningCheck(rec, 'dueDate')}
            handleSubmit={async (newDueDate) => {
              await updateInvoiceApi(rec.id, { dueDate: newDueDate })
              dispatch(getTenant(currentTenantId ?? ''))
            }}
          />
        ),
      }
    )

    if (showLateByColumn) {
      columns.push({
        label: 'Late By',
        headerTitle: 'Late By Date',
        columnWidth: 88,
        cellClassName: 'whitespace-nowrap',
        render: (rec) => (
          <InlineEditDatePicker
            name="lateByDate"
            label="Late By Date"
            hideLabel
            displayFormat="monthDay"
            className="min-w-[72px]"
            initialValue={rec.lateBy}
            minDate={dateFromUTCDateString(rec.due) ?? new Date(rec.due)}
            disabled={!rec.canEditDates || !hasEditInvoicePermission()}
            disabledReason={getDateEditDisabledReason(rec)}
            allowClear
            clearLabel="Same as Due"
            warningCheck={createDateWarningCheck(rec, 'lateByDate')}
            handleSubmit={async (newLateByDate) => {
              await updateInvoiceApi(rec.id, { lateByDate: newLateByDate })
              dispatch(getTenant(currentTenantId ?? ''))
            }}
          />
        ),
      })
    }

    columns.push(
      {
        label: 'Charges',
        headerTitle: 'Period Charges',
        align: 'right',
        columnWidth: 96,
        cellClassName: 'tabular-nums whitespace-nowrap',
        render: (rec) => formatInvoiceCurrency(rec.periodChargeAccrual),
      },
      {
        label: 'Period Due',
        headerTitle: 'Period Due Balance',
        align: 'right',
        columnWidth: 96,
        cellClassName: 'tabular-nums whitespace-nowrap',
        render: (rec) => formatInvoiceCurrency(rec.unpaidInvoiceBalance),
      },
      {
        label: 'DPD',
        headerTitle: 'Days Past Due When Paid',
        align: 'right',
        columnWidth: 64,
        cellClassName: 'tabular-nums whitespace-nowrap',
        render: (rec) => rec.daysPastDue,
      }
    )

    columns.push({
      label: 'Stmts',
      headerTitle: 'Statements (King, Utility, DOB, OptiRate)',
      columnWidth: statementColumnWidth,
      overflowVisible: true,
      cellClassName: 'whitespace-nowrap',
      render: (rec) => (
        <InvoiceStatementIcons
          invoice={rec}
          visibility={statementTypeVisibility}
          isPgeInvoice={isPgeAccount}
          dobAvailable={Boolean(currentTenantId && dobAvailability[rec.id])}
          onOpenKing={async () => await openStatement(rec.id, 'king')}
          onOpenUtility={async () => await openStatement(rec.id, 'utility')}
          onDownloadDob={async () => await handleDOBDownload(rec)}
          onOpenOptiRate={async () => await openStatement(rec.id, 'optirate_audit')}
        />
      ),
    })

    columns.push(
      {
        label: 'Method',
        headerTitle: 'Payment Method',
        columnWidth: 168,
        cellClassName: 'overflow-hidden',
        render: (rec) => {
          const method = formatPaymentMethod(rec)
          if (method == null) {
            return null
          }
          return <TruncatedCellWithTooltip text={method} maxWidthClass="max-w-[160px]" />
        },
      },
      {
        label: 'Memo',
        columnMinWidth: 160,
        cellClassName: 'overflow-visible',
        render: (rec) => (
          <InlineEditInput
            name="memo"
            label="Invoice memo"
            hideLabel
            className="min-w-[140px] max-w-[220px]"
            initialValue={rec.memo ?? ''}
            handleSubmit={async (editedValue) => {
              await updateInvoiceApi(rec.id, {
                memo: editedValue,
              })
              dispatch(getTenant(currentTenantId ?? ''))
            }}
          />
        ),
      }
    )

    return columns
  }, [
    currentTenantId,
    dispatch,
    dobAvailability,
    showActionsColumn,
    showLateByColumn,
    statementColumnWidth,
    statementTypeVisibility,
    toggleDropdown,
  ])

  const activeInvoice = activeDropdownId ? props.data.results.find((r) => r.id === activeDropdownId) : null
  const resultsPerPage = props.data.resultsPerPage ?? 5
  const atMaxRecords =
    props.data.results.length >= resultsPerPage ||
    (props.data.total != null && props.data.total > props.data.results.length)

  return (
    <>
      {props.data.results.length > 0 ? (
        <>
          {!props.invoicesEnriched && !enrichError && (
            <div className="mb-2 text-slate-500 dark:text-slate-400">Refreshing invoices…</div>
          )}
          {!props.invoicesEnriched && enrichError && (
            <div className="mb-2 text-red-600 dark:text-red-400">
              Could not refresh invoices.{' '}
              <button type="button" className="underline" onClick={() => setEnrichAttempt((n) => n + 1)}>
                Retry
              </button>
            </div>
          )}
          <Table<CollectionsInvoice>
            compact
            striped
            stickyHeader
            scrollShell={{ heightClassName: 'max-h-64 sm:max-h-80 md:max-h-96 lg:max-h-[45vh]' }}
            colConfig={colConfig}
            data={props.data}
            hidePaginator
            rowClassName={(rec) =>
              classnames(
                rec.isLatestInvoice && 'bg-sky-50/70 dark:bg-sky-950/25',
                MUTED_INVOICE_STATUSES.includes(rec.status) && 'text-slate-500 dark:text-slate-400'
              )
            }
          />
          {atMaxRecords && (
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={`/collections/invoices?tenantId=${currentTenantId ?? ''}&tenantAccountId=${props.tenantAccountId}`}
                className="inline-block rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
              >
                View all tenant account invoices
              </Link>
              <Link
                to={`/collections/invoices?tenantId=${currentTenantId ?? ''}`}
                className="inline-block rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
              >
                View all tenant invoices
              </Link>
            </div>
          )}
        </>
      ) : !props.invoicesEnriched && enrichError ? (
        <div className="text-red-600 dark:text-red-400">
          Could not load invoices.{' '}
          <button type="button" className="underline" onClick={() => setEnrichAttempt((n) => n + 1)}>
            Retry
          </button>
        </div>
      ) : !props.invoicesEnriched ? (
        <div className="text-slate-500 dark:text-slate-400">Loading invoices…</div>
      ) : (
        <div>An invoice has not yet been posted for this service account</div>
      )}

      {activeDropdownId && activeInvoice && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg py-1 min-w-[240px]"
          style={{
            left: dropdownCoords.x,
            top: dropdownCoords.y,
          }}
          ref={dropdownMenuRef}
        >
          <div className="py-1">
            {activeInvoice.nextStatus.map((status) => (
              <button
                key={status}
                onClick={async () => await handleNextStatusAction(activeInvoice, status)}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {status.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </div>
      )}

      {confirmModal}
    </>
  )
}

export default TabInvoices
